import { useEffect, useMemo, useState } from 'react';
import { defineMessages } from 'react-intl';
import { getAllMerchantConnections } from '../../api/vendor-api';
import { ProviderInviteStatusesLowerCase } from '../types';
import { extractExternalClientConnectionId, getArrayIfDataDoesNotExist, loadData } from '../utils';
import { useDataGrid } from './use-data-grid';

const messages = defineMessages({
  title: {
    description: 'Title for Error Pop Up when Providers failed to load.',
    defaultMessage: 'Failed to Load Providers',
    id: '9p1ulZ',
  },
  content: {
    description: 'Content for Error Pop Up when Providers failed to load.',
    defaultMessage: 'An unexpected error occurred while loading fulfillment services.',
    id: 'cmsKOR',
  },
});

const isExternalClientConnectionIdValid = (provider) => {
  return !!extractExternalClientConnectionId(provider);
};

const getProvidersExternalId = (providers) => {
  return providers.filter((provider) => isExternalClientConnectionIdValid(provider)).map(extractExternalClientConnectionId);
};

const getExternalInviteIdFromInvitations = (invitations) => {
  return invitations.map((invitation) => invitation.externalInviteId);
};

const getPendingInvitations = (invitations) =>
  invitations.filter((invitation) => (invitation.status ?? '').toLowerCase() === ProviderInviteStatusesLowerCase.pending);

const getFetchProvidersFn = (setProviders) => {
  const map = (providers) => {
    return (providers || []).map((provider) => ({
      ...provider,
      clientExternalConnectionId: extractExternalClientConnectionId(provider),
    }));
  };
  return () => {
    loadData(
      getAllMerchantConnections(),
      (data) =>
        setProviders({
          partners: map(data.partners),
          invitations: data.invitations,
        }),
      messages.title,
      messages.content,
    );
  };
};

export const useProviders = () => {
  const [data, setData] = useState({});

  useEffect(() => {
    getFetchProvidersFn(setData)();
  }, []);

  // this is memorized to make useDataGridProvider works only on reference change
  const invitations = useMemo(() => getPendingInvitations(getArrayIfDataDoesNotExist(data.invitations)), [data.invitations]);
  const externalInviteIds = useMemo(() => getExternalInviteIdFromInvitations(invitations), [invitations]);
  const clientExternalConnectionIds = useMemo(() => getProvidersExternalId(getArrayIfDataDoesNotExist(data.partners)), [data.partners]);

  const dataGridHandler = useDataGrid(externalInviteIds, clientExternalConnectionIds);

  return {
    list: getArrayIfDataDoesNotExist(data.partners),
    invitations,
    updateList: getFetchProvidersFn(setData),
    ...dataGridHandler,
  };
};
